import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TipTapLink from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import {
  StickyNote, Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, X, Check, Loader2,
} from "lucide-react";
import { toast } from "../ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NoteEditorDialog({ open, onClose, editingNote, negocioId, tc500, onSaved }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const noteEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TipTapLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: t("notes.placeholder") }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "focus:outline-none min-h-[160px] max-h-[300px] overflow-y-auto px-4 py-3 text-sm text-slate-800",
      },
    },
  });

  useEffect(() => {
    if (open && noteEditor) {
      noteEditor.commands.setContent(editingNote?.corpo_html || "");
    }
  }, [open, editingNote, noteEditor]);

  const handleSave = async () => {
    const html = noteEditor?.getHTML() || "";
    if (!html || html === "<p></p>") return;
    setSaving(true);
    try {
      if (editingNote && !editingNote._prefill) {
        await axios.put(`${API}/notes/${editingNote.id}`, { corpo_html: html }, { withCredentials: true });
      } else {
        await axios.post(`${API}/notes`, { negocio_id: negocioId, corpo_html: html }, { withCredentials: true });
      }
      toast.success(t("notes.saved"));
      onSaved();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  if (!noteEditor) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg !p-0">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            <StickyNote className="w-5 h-5" style={{ color: tc500 }} />
            {editingNote && !editingNote._prefill ? t("notes.editNote") : t("notes.add")}
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-5 space-y-3">
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
              <button type="button" onClick={() => noteEditor.chain().focus().toggleBold().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${noteEditor.isActive("bold") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <Bold className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => noteEditor.chain().focus().toggleItalic().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${noteEditor.isActive("italic") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <Italic className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => noteEditor.chain().focus().toggleUnderline().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${noteEditor.isActive("underline") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <UnderlineIcon className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => noteEditor.chain().focus().toggleStrike().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${noteEditor.isActive("strike") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <Strikethrough className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-5 bg-slate-300/50 mx-0.5" />
              <button type="button" onClick={() => noteEditor.chain().focus().toggleBulletList().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${noteEditor.isActive("bulletList") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <List className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => noteEditor.chain().focus().toggleOrderedList().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${noteEditor.isActive("orderedList") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <ListOrdered className="w-3.5 h-3.5" />
              </button>
            </div>
            <EditorContent editor={noteEditor} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              <X className="w-4 h-4 mr-1" />{t("common.cancel")}
            </Button>
            <Button type="button" className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }}
              disabled={saving} onClick={handleSave}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {t("notes.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
